/* What the stats page reads from the practice log: accuracy per spot, the list of sessions,
   and the hands of one session. Row-level security shows a user their own log and no other,
   so nothing here names the user.

   Accuracy is points over hands, as in the trainer: the page says what the sheets said. */

import { actionSchema, cellSchema, z, type Action, type Cell } from "@preflop/core";
import type { SupabaseClient } from "@supabase/supabase-js";

/* ---- rows ---- */

const spotStatRowSchema = z.object({
  spot_key: z.string(),
  hero: z.string().nullable(),
  action: z.string().nullable(),
  attempts: z.number().int(),
  correct: z.number().int(),
  points: z.number(),
  last_answered_at: z.string(),
  attempts_30d: z.number().int(),
  points_30d: z.number(),
  attempts_7d: z.number().int(),
  points_7d: z.number(),
  stakes: z.string().nullable(),
  stack: z.string().nullable(),
  open_size: z.string().nullable(),
});

const sessionRowSchema = z.object({
  id: z.uuid(),
  collection_id: z.uuid().nullable(),
  // Null when the collection is gone, or no longer one this user can read.
  collection: z.object({ name: z.string() }).nullable(),
  config: z.record(z.string(), z.unknown()),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  total: z.number().int(),
  correct: z.number().int(),
  points: z.number(),
});

const answerRowSchema = z.object({
  seq: z.number().int(),
  spot_id: z.uuid().nullable(),
  spot_key: z.string(),
  hand: z.string(),
  cards: z.string().nullable(),
  rnd: z.number().int(),
  cell: cellSchema,
  chosen: actionSchema,
  accepted: z.array(actionSchema),
  correct: z.boolean(),
  points: z.number(),
  ms: z.number().int().nullable(),
  answered_at: z.string(),
});

const weekRowSchema = z.object({
  week: z.iso.date(),
  action: z.string().nullable(),
  attempts: z.number().int(),
  points: z.number(),
});

const leakRowSchema = z.object({
  spot_key: z.string(),
  hand: z.string(),
  attempts: z.number().int(),
  points: z.number(),
  wrong: z.partialRecord(actionSchema, z.number().int()),
  cell: cellSchema,
  last_answered_at: z.string(),
  misses: z.array(
    z.object({
      rnd: z.number().int(),
      chosen: actionSchema,
      accepted: z.array(actionSchema),
      cell: cellSchema,
      cards: z.string().nullable(),
      answered_at: z.string(),
    }),
  ),
});

const globalRowSchema = z.object({
  spot_key: z.string(),
  users: z.number().int(),
  attempts: z.number().int(),
  accuracy: z.number(),
});

/** Everyone's accuracy on one spot: the mean of the players' own, never of fewer than 20. */
export interface GlobalStat {
  spotKey: string;
  /** How many players count: those with 5 hands or more on the spot. */
  users: number;
  attempts: number;
  /** Out of 100, rounded as every accuracy on the page is. */
  accuracy: number;
}

/** A player counts once they have this many hands on a spot, and a spot is shown once this
    many players count. The database applies both; these are for the page to say. */
export const GLOBAL_MIN_HANDS = 5;
export const GLOBAL_MIN_USERS = 20;

export const globalStatFromRow = (raw: unknown): GlobalStat => {
  const r = globalRowSchema.parse(raw);
  return {
    spotKey: r.spot_key,
    users: r.users,
    attempts: r.attempts,
    accuracy: Math.round(100 * r.accuracy),
  };
};

/** Hands and points over one period. */
export interface Tally {
  attempts: number;
  points: number;
}

/** "recent" is the last 30 days, "week" the last 7. The view counts them; the leak finder
    is asked with a date. */
export type Period = "all" | "recent" | "week";
export const PERIOD_DAYS: Readonly<Record<Period, number | null>> = {
  all: null,
  recent: 30,
  week: 7,
};
/** How far back "recent" goes: what drilling one's mistakes weighs on. */
export const RECENT_DAYS = 30;

export interface SpotStat {
  spotKey: string;
  /** Null for a spot whose answers all arrived after it had gone. */
  hero: string | null;
  action: string | null;
  stakes: string | null;
  stack: string | null;
  openSize: string | null;
  correct: number;
  lastAnsweredAt: string;
  all: Tally;
  recent: Tally;
  week: Tally;
}

export interface SessionSummary {
  id: string;
  collectionId: string | null;
  collectionName: string | null;
  config: Record<string, unknown>;
  startedAt: string;
  finishedAt: string | null;
  total: number;
  correct: number;
  points: number;
}

export interface LoggedAnswer {
  seq: number;
  spotId: string | null;
  spotKey: string;
  hand: string;
  /** As dealt, "AhKh". */
  cards: string | null;
  rnd: number;
  /** The hand's frequencies as they were when it was asked. */
  cell: Cell;
  chosen: Action;
  accepted: Action[];
  correct: boolean;
  points: number;
  ms: number | null;
  answeredAt: string;
}

/** One action's hands in one week. The week is its Monday, "2026-09-14", in UTC. */
export interface WeekRow {
  week: string;
  /** Null for answers that arrived after their spot had gone. */
  action: string | null;
  tally: Tally;
}

/** A hand class in a spot that has cost points. */
export interface Leak {
  spotKey: string;
  hand: string;
  tally: Tally;
  /** What was answered when it was wrong, and how often. */
  wrong: Partial<Record<Action, number>>;
  /** What the chart played the last time the hand was asked. */
  cell: Cell;
  lastAnsweredAt: string;
  /** The latest wrong answers, newest first, five at the most, each with its roll. */
  misses: LeakMiss[];
}

/** One wrong answer behind a leak: the roll is what made it wrong, on a mixed hand. */
export interface LeakMiss {
  rnd: number;
  chosen: Action;
  accepted: Action[];
  cell: Cell;
  cards: string | null;
  answeredAt: string;
}

/** Where a leak was played. A field left out, or null, is "all of them". */
export interface LeakFilters {
  hero?: string | null;
  action?: string | null;
  stakes?: string | null;
  stack?: string | null;
  openSize?: string | null;
}

export const weekFromRow = (raw: unknown): WeekRow => {
  const r = weekRowSchema.parse(raw);
  return { week: r.week, action: r.action, tally: { attempts: r.attempts, points: r.points } };
};

export const leakFromRow = (raw: unknown): Leak => {
  const r = leakRowSchema.parse(raw);
  return {
    spotKey: r.spot_key,
    hand: r.hand,
    tally: { attempts: r.attempts, points: r.points },
    wrong: r.wrong,
    cell: r.cell,
    lastAnsweredAt: r.last_answered_at,
    misses: r.misses.map((m) => ({
      rnd: m.rnd,
      chosen: m.chosen,
      accepted: m.accepted,
      cell: m.cell,
      cards: m.cards,
      answeredAt: m.answered_at,
    })),
  };
};

export const spotStatFromRow = (raw: unknown): SpotStat => {
  const r = spotStatRowSchema.parse(raw);
  return {
    spotKey: r.spot_key,
    hero: r.hero,
    action: r.action,
    correct: r.correct,
    lastAnsweredAt: r.last_answered_at,
    stakes: r.stakes,
    stack: r.stack,
    openSize: r.open_size,
    all: { attempts: r.attempts, points: r.points },
    recent: { attempts: r.attempts_30d, points: r.points_30d },
    week: { attempts: r.attempts_7d, points: r.points_7d },
  };
};

export const sessionFromRow = (raw: unknown): SessionSummary => {
  const r = sessionRowSchema.parse(raw);
  return {
    id: r.id,
    collectionId: r.collection_id,
    collectionName: r.collection?.name ?? null,
    config: r.config,
    startedAt: r.started_at,
    finishedAt: r.finished_at,
    total: r.total,
    correct: r.correct,
    points: r.points,
  };
};

export const answerFromRow = (raw: unknown): LoggedAnswer => {
  const r = answerRowSchema.parse(raw);
  return {
    seq: r.seq,
    spotId: r.spot_id,
    spotKey: r.spot_key,
    hand: r.hand,
    cards: r.cards,
    rnd: r.rnd,
    cell: r.cell,
    chosen: r.chosen,
    accepted: r.accepted,
    correct: r.correct,
    points: r.points,
    ms: r.ms,
    answeredAt: r.answered_at,
  };
};

/* ---- sums ---- */

/** Accuracy out of 100, rounded as the trainer rounds it; null with no hand to speak of. */
export const accuracyOf = (t: Tally): number | null =>
  t.attempts ? Math.round((100 * t.points) / t.attempts) : null;

export const sumTallies = (list: readonly Tally[]): Tally =>
  list.reduce(
    (sum, t) => ({ attempts: sum.attempts + t.attempts, points: sum.points + t.points }),
    {
      attempts: 0,
      points: 0,
    },
  );

/** What an answer that arrived after its spot had gone is counted under. */
export const OTHER = "Other";

/**
 * The spots' tallies summed by action or by position, over one period. Groups with no hand
 * in the period are left out. `order` is the order the groups are listed in; a group it does
 * not know comes after the ones it does, and "Other" last.
 */
export function tallyBy(
  stats: readonly SpotStat[],
  by: "action" | "hero",
  period: Period,
  order: readonly string[],
): { name: string; tally: Tally }[] {
  const groups = new Map<string, Tally[]>();
  for (const s of stats) {
    if (!s[period].attempts) continue;
    const name = s[by] ?? OTHER;
    groups.set(name, [...(groups.get(name) ?? []), s[period]]);
  }
  const rank = (name: string): number => {
    if (name === OTHER) return order.length + 1;
    const at = order.indexOf(name);
    return at < 0 ? order.length : at;
  };
  return [...groups]
    .map(([name, list]) => ({ name, tally: sumTallies(list) }))
    .sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));
}

/** The spots played in a period, the ones with the most points lost first: that is where the
    most is to be gained, which neither the worst accuracy (one hand, missed) nor the most
    hands says. Ties go to the spot played more, then to the key. */
export function mostToGain(stats: readonly SpotStat[], period: Period): SpotStat[] {
  const lost = (s: SpotStat): number => s[period].attempts - s[period].points;
  return stats
    .filter((s) => s[period].attempts > 0)
    .sort(
      (a, b) =>
        lost(b) - lost(a) ||
        b[period].attempts - a[period].attempts ||
        a.spotKey.localeCompare(b.spotKey),
    );
}

/* ---- weeks ---- */

const DAY_MS = 86_400_000;
/** The Monday after this one, as the view writes a week. */
const nextWeek = (week: string): string =>
  new Date(Date.parse(`${week}T00:00:00Z`) + 7 * DAY_MS).toISOString().slice(0, 10);

export interface WeeklySeries {
  /** Every week from the first played to the last, played or not, oldest first. */
  weeks: string[];
  /** All actions together: one tally per week, null for a week with no answer. */
  overall: (Tally | null)[];
  /** The same per action, under the action's stored name; "Other" for the placeless. */
  byAction: Map<string, (Tally | null)[]>;
}

/**
 * The view's rows as lines to draw. A week nobody played is a gap, not a zero: the line is
 * broken there, since an accuracy of nothing over nothing is not 0%. `last` keeps the most
 * recent weeks only.
 */
export function weeklySeries(rows: readonly WeekRow[], last = 52): WeeklySeries {
  const played = [...new Set(rows.map((r) => r.week))].sort();
  const weeks: string[] = [];
  const end = played.at(-1);
  for (let w = played[0]; w && end && w <= end; w = nextWeek(w)) weeks.push(w);
  const kept = weeks.slice(-last);
  const at = new Map(kept.map((w, i) => [w, i]));

  const empty = (): (Tally | null)[] => kept.map(() => null);
  const overall = empty();
  const byAction = new Map<string, (Tally | null)[]>();
  for (const r of rows) {
    const i = at.get(r.week);
    if (i === undefined || !r.tally.attempts) continue;
    const name = r.action ?? OTHER;
    const line = byAction.get(name) ?? empty();
    byAction.set(name, line);
    line[i] = sumTallies([line[i] ?? { attempts: 0, points: 0 }, r.tally]);
    overall[i] = sumTallies([overall[i] ?? { attempts: 0, points: 0 }, r.tally]);
  }
  return { weeks: kept, overall, byAction };
}

/* ---- reads ---- */

export interface StatsApi {
  /** One row per spot the user has ever answered. */
  spotStats(): Promise<SpotStat[]>;
  /** Sessions with at least one answer in the database, the newest first. `before` is the
      `startedAt` of the last one already shown. */
  listSessions(options: { limit: number; before?: string }): Promise<SessionSummary[]>;
  /** Every hand of one session, in the order they were asked. */
  sessionAnswers(sessionId: string): Promise<LoggedAnswer[]>;
  /** Hands and points per week and per action, the oldest week first. */
  weeks(): Promise<WeekRow[]>;
  /** Everyone's accuracy per spot, by spot key: only the spots enough players have answered.
      Refreshed nightly, so up to a day old. */
  globalStats(): Promise<Map<string, GlobalStat>>;
  /** The hands that have cost the most points over a period, the costliest first. */
  leaks(options: { period: Period; limit: number; filters?: LeakFilters }): Promise<Leak[]>;
}

const SPOT_STAT_COLUMNS =
  "spot_key, hero, action, stakes, stack, open_size, attempts, correct, points, last_answered_at, attempts_30d, points_30d, attempts_7d, points_7d";
const SESSION_COLUMNS =
  "id, collection_id, collection:collections(name), config, started_at, finished_at, total, correct, points";
const ANSWER_COLUMNS =
  "seq, spot_id, spot_key, hand, cards, rnd, cell, chosen, accepted, correct, points, ms, answered_at";
// PostgREST caps a response at 1,000 rows: a session holds that many answers at the most, and
// an account may have answered more spots than that.
const PAGE = 1000;

export function createStatsApi(db: SupabaseClient): StatsApi {
  return {
    async spotStats() {
      const out: SpotStat[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await db
          .from("spot_stats")
          .select(SPOT_STAT_COLUMNS)
          .order("spot_key")
          .range(from, from + PAGE - 1);
        if (error) throw new Error(`Could not read your stats: ${error.message}`);
        out.push(...data.map(spotStatFromRow));
        if (data.length < PAGE) return out;
      }
    },

    async listSessions({ limit, before }) {
      let query = db.from("practice_sessions").select(SESSION_COLUMNS).gt("total", 0);
      if (before) query = query.lt("started_at", before);
      const { data, error } = await query.order("started_at", { ascending: false }).limit(limit);
      if (error) throw new Error(`Could not read your sessions: ${error.message}`);
      return data.map(sessionFromRow);
    },

    async globalStats() {
      const out = new Map<string, GlobalStat>();
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await db
          .from("spot_stats_global")
          .select("spot_key, users, attempts, accuracy")
          .order("spot_key")
          .range(from, from + PAGE - 1);
        if (error) throw new Error(`Could not read everyone's averages: ${error.message}`);
        for (const row of data.map(globalStatFromRow)) out.set(row.spotKey, row);
        if (data.length < PAGE) return out;
      }
    },

    async weeks() {
      const out: WeekRow[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await db
          .from("practice_weeks")
          .select("week, action, attempts, points")
          .order("week")
          .order("action")
          .range(from, from + PAGE - 1);
        if (error) throw new Error(`Could not read your progression: ${error.message}`);
        out.push(...data.map(weekFromRow));
        if (data.length < PAGE) return out;
      }
    },

    async leaks({ period, limit, filters = {} }) {
      const days = PERIOD_DAYS[period];
      const { data, error } = await db.rpc("practice_leaks", {
        since: days === null ? null : new Date(Date.now() - days * DAY_MS).toISOString(),
        max_rows: limit,
        of_hero: filters.hero ?? null,
        of_action: filters.action ?? null,
        of_stakes: filters.stakes ?? null,
        of_stack: filters.stack ?? null,
        of_open: filters.openSize ?? null,
      });
      if (error) throw new Error(`Could not read your leaks: ${error.message}`);
      return (data as unknown[]).map(leakFromRow);
    },

    async sessionAnswers(sessionId) {
      const { data, error } = await db
        .from("practice_answers")
        .select(ANSWER_COLUMNS)
        .eq("session_id", sessionId)
        .order("seq")
        .limit(PAGE);
      if (error) throw new Error(`Could not read that session: ${error.message}`);
      return data.map(answerFromRow);
    },
  };
}
